import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { getSearchIndex } from "../../api/search";

function SearchComponent({ onNavigate }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [searchItems, setSearchItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadError, setHasLoadError] = useState(false);
  const navigate = useNavigate();

  const loadSearchItems = useCallback(async () => {
    if (searchItems.length > 0 || isLoading) {
      return;
    }

    setIsLoading(true);
    setHasLoadError(false);

    try {
      const items = await getSearchIndex();
      setSearchItems(items);
    } catch {
      setHasLoadError(true);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, searchItems.length]);

  const filteredData = useMemo(() => {
    const value = searchTerm.trim().toLowerCase();
    if (!value) {
      return [];
    }

    return searchItems.filter((item) => {
      const title = String(item.title || "").toLowerCase();
      return title.includes(value);
    });
  }, [searchItems, searchTerm]);

  const navigateTo = (link) => {
    navigate(link);
    setSearchTerm("");
    setIsFocused(false);
    onNavigate?.();
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (filteredData.length > 0) {
      navigateTo(filteredData[0].link);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    loadSearchItems();
  };

  const handleChange = (event) => {
    setSearchTerm(event.target.value);
    if (event.target.value.trim()) {
      loadSearchItems();
    }
  };

  const showResults = isFocused && searchTerm.trim() !== "";

  return (
    <Box className="app-search">
      <Box component="form" onSubmit={handleSubmit} className="app-search__form">
        <TextField
          fullWidth
          size="small"
          value={searchTerm}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={() => setTimeout(() => setIsFocused(false), 120)}
          placeholder="Пошук..."
          autoComplete="off"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton type="submit" edge="end" aria-label="Шукати">
                  <SearchRoundedIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {showResults && (
        <Paper className="app-search__results" elevation={6}>
          <List dense disablePadding>
            {filteredData.map((item) => (
              <ListItemButton key={item.id} onMouseDown={() => navigateTo(item.link)}>
                <ListItemText primary={item.title} />
              </ListItemButton>
            ))}
            {isLoading && (
              <Typography className="app-search__empty" variant="body2">
                Завантаження...
              </Typography>
            )}
            {!isLoading && hasLoadError && (
              <Typography className="app-search__empty" variant="body2">
                Пошук тимчасово недоступний
              </Typography>
            )}
            {!isLoading && !hasLoadError && filteredData.length === 0 && (
              <Typography className="app-search__empty" variant="body2">
                Нічого не знайдено
              </Typography>
            )}
          </List>
        </Paper>
      )}
    </Box>
  );
}

export default SearchComponent;
